import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useTour } from './TourContext';
import { cn } from '@/lib/utils';
import {
  getSpotlightGeometry,
  shouldScrollTargetIntoView,
  computeCachedMetrics,
  projectSpotlight,
  type SpotlightCachedMetrics,
} from './spotlightGeometry';

/**
 * Renders a spotlight overlay highlighting the current tour step target,
 * plus an anchored tooltip with title, text, and navigation buttons.
 *
 * Monitors target element presence and hides when element disappears.
 * If the element never appears after retries, silently advances to the next step.
 */

const MAX_RETRIES = 20;
const RETRY_INTERVAL = 250;
/** How often (ms) to check the target element still exists when spotlight is visible */
const PRESENCE_CHECK_INTERVAL = 400;

export function TourSpotlight() {
  const { t } = useTranslation();
  const { status, currentStep, currentStepIndex, totalSteps, nextStep, prevStep, skipTour, currentPage } = useTour();
  const [spotlight, setSpotlight] = useState<ReturnType<typeof getSpotlightGeometry> | null>(null);
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const maskRectRef = useRef<SVGRectElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const cachedMetricsRef = useRef<SpotlightCachedMetrics | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();
  const trackingRafRef = useRef<number>();

  // Reset visibility when step changes
  useEffect(() => {
    setVisible(false);
    setSpotlight(null);
    targetRef.current = null;
    cachedMetricsRef.current = null;
    retryCountRef.current = 0;
  }, [currentStepIndex, currentStep?.id]);

  const getTooltipPosition = useCallback((rect: DOMRect) => {
    if (!currentStep) return null;

    const padding = 12;
    const tooltipWidth = Math.min(320, window.innerWidth - 32);
    const placement = currentStep.placement || 'bottom';

    let top = 0;
    let left = 0;

    if (placement === 'bottom') {
      top = rect.bottom + padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'top') {
      top = rect.top - padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2;
      left = rect.right + padding;
    } else {
      top = rect.top + rect.height / 2;
      left = rect.left - tooltipWidth - padding;
    }

    // Keep within viewport
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    if (placement === 'bottom' && top + 200 > window.innerHeight) {
      top = rect.top - padding - 200;
    }
    if (placement === 'top') {
      top = top - 200;
    }
    if (top < 16) top = rect.bottom + padding;

    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      zIndex: 10002,
    };
  }, [currentStep]);

  const positionTooltip = useCallback((rect: DOMRect, syncState = false) => {
    const nextStyle = getTooltipPosition(rect);
    if (!nextStyle) return;

    const tooltip = tooltipRef.current;
    if (tooltip) {
      tooltip.style.position = String(nextStyle.position);
      tooltip.style.top = String(nextStyle.top);
      tooltip.style.left = String(nextStyle.left);
      tooltip.style.width = String(nextStyle.width);
      tooltip.style.zIndex = String(nextStyle.zIndex);
    }

    if (syncState) {
      setTooltipStyle(nextStyle);
    }
  }, [getTooltipPosition]);

  const applyGeometryToDOM = useCallback((geometry: ReturnType<typeof getSpotlightGeometry>) => {
    const maskRect = maskRectRef.current;
    if (maskRect) {
      maskRect.setAttribute('x', String(geometry.mask.x));
      maskRect.setAttribute('y', String(geometry.mask.y));
      maskRect.setAttribute('width', String(geometry.mask.width));
      maskRect.setAttribute('height', String(geometry.mask.height));
      maskRect.setAttribute('rx', String(geometry.mask.rx));
      maskRect.setAttribute('ry', String(geometry.mask.ry));
    }

    const ring = ringRef.current;
    if (ring) {
      ring.style.top = `${geometry.frame.top}px`;
      ring.style.left = `${geometry.frame.left}px`;
      ring.style.width = `${geometry.frame.width}px`;
      ring.style.height = `${geometry.frame.height}px`;
      ring.style.borderRadius = geometry.frame.borderRadius;
    }
  }, []);

  /** Full sync: recompute expensive metrics + position. Used on step acquire. */
  const syncSpotlightToTarget = useCallback((target: HTMLElement, syncState = false) => {
    const cached = computeCachedMetrics(target);
    cachedMetricsRef.current = cached;
    const geometry = projectSpotlight(target, cached);
    applyGeometryToDOM(geometry);
    positionTooltip(geometry.targetRect, syncState);

    if (syncState) {
      setSpotlight(geometry);
    }
  }, [applyGeometryToDOM, positionTooltip]);

  const findTarget = useCallback(() => {
    if (status !== 'active' || !currentStep) return;

    // Only look for elements on the correct page
    if (currentPage && currentStep.page !== currentPage) return;

    const el = document.querySelector(currentStep.targetSelector) as HTMLElement | null;
    if (!el) {
      retryCountRef.current += 1;
      if (retryCountRef.current >= MAX_RETRIES) {
        // Element never appeared – advance to next step
        nextStep();
        return;
      }
      retryTimerRef.current = setTimeout(findTarget, RETRY_INTERVAL);
      return;
    }

    targetRef.current = el;

    // Found the element – scroll into view, then position
    const initialRect = el.getBoundingClientRect();
    const needsScroll = shouldScrollTargetIntoView(initialRect);
    el.scrollIntoView({ behavior: needsScroll ? 'smooth' : 'auto', block: 'center', inline: 'nearest' });

    const positionTarget = () => {
      rafRef.current = requestAnimationFrame(() => {
        if (!document.body.contains(el)) return;
        targetRef.current = el;
        syncSpotlightToTarget(el, true);
        setVisible(true);
      });
    };

    if (needsScroll) {
      settleTimerRef.current = setTimeout(positionTarget, 180);
      return;
    }

    positionTarget();
  }, [status, currentStep, currentPage, nextStep, syncSpotlightToTarget]);

  // Find target whenever step or page changes
  useEffect(() => {
    if (status !== 'active' || !currentStep) return;
    if (currentPage && currentStep.page !== currentPage) return;

    const timer = setTimeout(findTarget, 200);

    return () => {
      clearTimeout(timer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [status, currentStep, currentPage, findTarget]);

  // Monitor target element presence – hide spotlight if element disappears
  useEffect(() => {
    if (!visible || status !== 'active' || !currentStep) return;

    const interval = setInterval(() => {
      const el = document.querySelector(currentStep.targetSelector);
      if (!el) {
        // Element disappeared (user navigated away) – hide and restart find
        setVisible(false);
        setSpotlight(null);
        targetRef.current = null;
        retryCountRef.current = 0;
        clearInterval(interval);
        // Restart findTarget which will exhaust retries and auto-advance
        retryTimerRef.current = setTimeout(findTarget, RETRY_INTERVAL);
      }
    }, PRESENCE_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [visible, status, currentStep, findTarget]);

  // Recompute cached metrics when target resizes or window resizes
  useEffect(() => {
    if (!visible || status !== 'active') return;
    const target = targetRef.current;
    if (!target) return;

    const recompute = () => {
      if (targetRef.current && document.body.contains(targetRef.current)) {
        cachedMetricsRef.current = computeCachedMetrics(targetRef.current);
      }
    };

    const ro = new ResizeObserver(recompute);
    ro.observe(target);
    window.addEventListener('resize', recompute);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [visible, status, currentStepIndex]);

  // Track the target continuously while visible — cheap projection only (no neighbour scanning)
  useEffect(() => {
    if (!visible || status !== 'active' || !currentStep) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const cached = cachedMetricsRef.current;
      const existingTarget = targetRef.current;
      const target = existingTarget && document.body.contains(existingTarget)
        ? existingTarget
        : document.querySelector(currentStep.targetSelector) as HTMLElement | null;

      if (target && cached) {
        targetRef.current = target;
        const geometry = projectSpotlight(target, cached);
        applyGeometryToDOM(geometry);
        positionTooltip(geometry.targetRect);
      }

      trackingRafRef.current = requestAnimationFrame(tick);
    };

    trackingRafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (trackingRafRef.current) cancelAnimationFrame(trackingRafRef.current);
    };
  }, [visible, status, currentStep, applyGeometryToDOM, positionTooltip]);

  // Don't render anything until we have a positioned target
  if (status !== 'active' || !currentStep || !spotlight || !visible) return null;
  if (currentPage && currentStep.page !== currentPage) return null;

  return createPortal(
    <>
      {/* Dark overlay with rounded spotlight cutout */}
      <svg className="fixed inset-0 z-[10000] pointer-events-none w-full h-full" aria-hidden="true">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              ref={maskRectRef}
              x={spotlight.mask.x}
              y={spotlight.mask.y}
              width={spotlight.mask.width}
              height={spotlight.mask.height}
              rx={spotlight.mask.rx}
              ry={spotlight.mask.ry}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.5)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>
      {/* Spotlight ring */}
      <div
        ref={ringRef}
        className="fixed z-[10001] pointer-events-none ring-2 ring-primary/40"
        style={{
          top: spotlight.frame.top,
          left: spotlight.frame.left,
          width: spotlight.frame.width,
          height: spotlight.frame.height,
          borderRadius: spotlight.frame.borderRadius,
        }}
      />
      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className={cn(
          'rounded-xl border border-border bg-card shadow-lg p-4 animate-in fade-in-0 zoom-in-95',
          'pointer-events-auto'
        )}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-foreground">{t(currentStep.title)}</h3>
          <button
            onClick={skipTour}
            className="shrink-0 p-0.5 rounded-md hover:bg-muted transition-colors"
            aria-label="Skip tour"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{t(currentStep.text)}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            {currentStepIndex + 1} / {totalSteps}
          </span>
          <div className="flex items-center gap-1.5">
            {currentStepIndex > 0 && !currentStep.clickTarget && (
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={prevStep}>
                {t('back')}
              </Button>
            )}
            {!currentStep.hideCta && (
              <Button size="sm" className="h-7 text-xs px-3" onClick={() => {
                if (currentStep.clickTarget) {
                  const target = document.querySelector(currentStep.targetSelector) as HTMLElement | null;
                  if (target) target.click();
                  // Advance after a short delay to let navigation happen
                  setTimeout(() => nextStep(), 100);
                } else {
                  nextStep();
                }
              }}>
                {currentStep.ctaLabel ? t(currentStep.ctaLabel) : (currentStepIndex === totalSteps - 1 ? t('tour.nav_finish') : t('tour.nav_next'))}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
